import { z } from 'zod';
/**
 * Widget REST DTO — Phase 5 / Plan 05-01 (WIDGET-01 + WIDGET-06).
 *
 * 엔드포인트:
 *   - `POST   /api/v1/widgets/me/token`              — 활성 토큰 issue (멱등: 이미 있으면 기존 반환)
 *   - `POST   /api/v1/widgets/me/regenerate-token`   — prev 토큰 즉시 revoke + 새 토큰 발급
 *   - `GET    /api/v1/widgets/me/token`              — 현재 활성 토큰 조회 (없으면 null)
 *
 * URL 포맷 (DESIGN.md §3):
 *   `${WIDGET_PUBLIC_URL}/widget?streamerId=<uuid>&token=<jwt>`
 *
 * Token TTL: 10년 (effectively no-expiry) — revocation 은 `jti` 기반 Redis SET + DB fallback.
 */
/** 발급 응답 — issue / regenerate / (get-current 가 not-null 일 때) 공용 */
export const issueWidgetTokenResponseSchema = z.object({
    /** widget_tokens.id (PK) — widget 인스턴스 식별자. */
    widgetId: z.string().uuid(),
    /** 대상 스트리머 id. JWT payload.streamerId 와 동일. */
    streamerId: z.string().uuid(),
    /** 발급된 JWT 의 `jti` claim — revocation 단위. */
    jti: z.string().uuid(),
    /** JWT 원문 (HS256 HMAC). OBS 브라우저 소스 URL 쿼리에 박힘. */
    token: z.string().min(1),
    /** 완전체 OBS 위젯 URL — `${WIDGET_PUBLIC_URL}/widget?streamerId=<uuid>&token=<jwt>`. */
    url: z.string().url(),
    /** ISO8601 with offset. */
    issuedAt: z.string().datetime({ offset: true }),
    /** ISO8601 with offset. Wave 0: issuedAt + 10y. */
    expiresAt: z.string().datetime({ offset: true }),
});
/** POST /regenerate-token request body — 이유 기록 (optional, audit 목적). */
export const regenerateWidgetTokenRequestSchema = z.object({
    /** 재발급 사유 (선택). 'compromised' | 'rotation' | 자유 텍스트. audit 원장에 기록. */
    reason: z.string().max(200).optional(),
});
/** GET /token 응답 — 활성 토큰 없으면 null. */
export const getCurrentWidgetTokenResponseSchema = issueWidgetTokenResponseSchema.nullable();
/* ------------------------------------------------------------------------- */
/* Simulate Donation — Plan 06-04 Task 1 (DASH-05)                           */
/* ------------------------------------------------------------------------- */
/**
 * Widget simulate-donation DTO — Plan 06-04 Task 1.
 *
 * 엔드포인트: `POST /api/v1/widgets/me/simulate-donation`
 *
 * 용도: 스트리머가 "샘플 후원 재생" 버튼을 누르면 서버가 본인 streamer 의
 *      `widget:{streamerId}` 채널로만 `donation.completed.v1` envelope 을
 *      broadcast (실 결제 · ledger 영향 없음). 위젯 iframe 애니메이션/프리뷰
 *      파이프라인 검증용.
 *
 * Env gate (T-06-04-09): NODE_ENV=production 이고 ALLOW_SIMULATE_DONATION!='true'
 *   이면 controller 레벨에서 403 `simulation_disabled`.
 *
 * Rate limit (T-06-04-02): streamer 당 10 초 / 5 회 제한 → 429 `rate_limited`.
 */
export const simulateDonationRequestSchema = z.object({
    tier: z.enum(['small', 'medium', 'large']),
    /** 1..32 chars. default '샘플팬' — 서버에서 default 적용. */
    nickname: z.string().min(1).max(32).default('샘플팬'),
    /** max 200, null 허용 (optional). */
    message: z.string().max(200).nullable().optional(),
});
export const simulateDonationResponseSchema = z.object({
    donationId: z.string().uuid(),
    broadcastedAt: z.string().datetime({ offset: true }),
    /** `widget:${streamerId}` — 세션 기반이라 응답에 포함해도 식별 노출 없음 (본인). */
    channel: z.string(),
});
//# sourceMappingURL=widget.rest.js.map